/* eslint-disable no-use-before-define, no-return-assign */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';
import {
  FlavorDetails,
  FlavorRepository,
  BrandRepository,
  SeriesRepository,
  DbBrand,
  DbSeries,
} from '../repositories/LocalDatabase';
import FlavorCard from './FlavorCard';
import AddFlavorModal from './AddFlavorModal';
import FlavorDetailModal from './FlavorDetailModal';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import { showHUD } from './NotificationHUD';

export default function FlavorList() {
  const location = useLocation();
  const [flavors, setFlavors] = useState<FlavorDetails[]>([]);
  const [brands, setBrands] = useState<DbBrand[]>([]);
  const [series, setSeries] = useState<DbSeries[]>([]);

  // Search & Filters state
  const [searchQuery, setSearchQuery] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState<
    'all' | 'owned' | 'unowned'
  >('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedSeries, setSelectedSeries] = useState<string>('all');
  const [nicotineFilter, setNicotineFilter] = useState<
    'all' | 'nicotine' | 'nonNicotine'
  >('all');
  const [sortBy, setSortBy] = useState<'name' | 'brand' | 'rating'>('name');

  // Modals state
  const [selectedFlavor, setSelectedFlavor] = useState<FlavorDetails | null>(
    null,
  );
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [initialScannedText, setInitialScannedText] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  // ページボタンの表示ウィンドウ開始ページ（1-indexed）
  const [pageWindowStart, setPageWindowStart] = useState(1);

  // 一括登録モード
  const [isQuickEditMode, setIsQuickEditMode] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
    setPageWindowStart(1);
  }, [searchQuery, ownershipFilter, selectedBrand, selectedSeries, nicotineFilter, sortBy]);

  useEffect(() => {
    loadData();

    // Check if we navigated here with openAddModal request
    if ((location.state as any)?.openAddModal) {
      setIsAddModalOpen(true);
      if ((location.state as any)?.scannedText) {
        setInitialScannedText((location.state as any).scannedText);
      }
      // Clean location state to avoid reopening on refresh/back
      window.history.replaceState({}, document.title);
    }

    // Listen to the custom event dispatched by BottomNav's menu
    const handleOpenAddModal = (e: Event) => {
      setIsAddModalOpen(true);
      const customEvent = e as CustomEvent;
      if (customEvent?.detail?.scannedText) {
        setInitialScannedText(customEvent.detail.scannedText);
      } else {
        setInitialScannedText(undefined);
      }
    };
    window.addEventListener('open-add-flavor-modal', handleOpenAddModal);
    return () => {
      window.removeEventListener('open-add-flavor-modal', handleOpenAddModal);
    };
  }, [location.state]);

  const loadData = async () => {
    const list = await FlavorRepository.getAll();
    const brandList = await BrandRepository.getAll();
    const seriesList = await SeriesRepository.getAll();

    setFlavors(list);
    setBrands(brandList);
    setSeries(seriesList);
  };

  // Filtered & Sorted Flavors
  const processedFlavors = useMemo(() => {
    let result = [...flavors];

    // 1. Search Query (name, brand, description)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(query) ||
          f.brand_name.toLowerCase().includes(query) ||
          (f.description && f.description.toLowerCase().includes(query)),
      );
    }

    // 2. Ownership Filter
    if (ownershipFilter === 'owned') {
      result = result.filter((f) => f.owned);
    } else if (ownershipFilter === 'unowned') {
      result = result.filter((f) => !f.owned);
    }

    // 3. Brand Filter
    if (selectedBrand !== 'all') {
      result = result.filter((f) => f.brand_name === selectedBrand);
    }

    // 4. Series (Category) Filter
    if (selectedSeries !== 'all') {
      result = result.filter((f) => f.series_id === selectedSeries);
    }

    // 4.5 Nicotine Filter
    if (nicotineFilter === 'nicotine') {
      result = result.filter((f) => f.nicotine);
    } else if (nicotineFilter === 'nonNicotine') {
      result = result.filter((f) => !f.nicotine);
    }

    // 5. Sort By
    result.sort((a, b) => {
      if (sortBy === 'brand') {
        const brandComp = a.brand_name.localeCompare(b.brand_name, 'ja');
        if (brandComp !== 0) return brandComp;
        return a.name.localeCompare(b.name, 'ja');
      }
      if (sortBy === 'rating') {
        const ratingA = a.owned ? a.rating || 0 : -1;
        const ratingB = b.owned ? b.rating || 0 : -1;
        if (ratingB !== ratingA) return ratingB - ratingA;
        return a.name.localeCompare(b.name, 'ja');
      }
      // default: name
      return a.name.localeCompare(b.name, 'ja');
    });

    return result;
  }, [
    flavors,
    searchQuery,
    ownershipFilter,
    selectedBrand,
    selectedSeries,
    nicotineFilter,
    sortBy,
  ]);

  const itemsPerPage = 8;
  const totalPages = Math.ceil(processedFlavors.length / itemsPerPage);

  const paginatedFlavors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return processedFlavors.slice(startIndex, startIndex + itemsPerPage);
  }, [processedFlavors, currentPage]);

  const handleBulkUnown = async () => {
    if (processedFlavors.length === 0) return;
    setConfirmModalConfig({
      isOpen: true,
      title: '確認',
      message: `表示されている${processedFlavors.length}件のフレーバーを全て未所持にしますか？\n(※この操作は元に戻せません)`,
      onConfirm: async () => {
        try {
          const flavorIds = processedFlavors.map((f) => f.id);
          await FlavorRepository.bulkSetUnowned(flavorIds);
          await loadData();
        } catch (err) {
          console.error('Failed to bulk unown flavors:', err);
          showHUD({ type: 'error', message: 'エラーが発生しました。' });
        } finally {
          setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    padding: '40px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    maxWidth: '1000px',
    margin: '0 auto',
    animation: 'fadeIn 0.5s ease-out',
    paddingBottom: '100px', // Extra padding for BottomNav spacing
  };

  const pageHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: 'var(--font-heading-ja)',
    fontSize: 'var(--font-size-h1)',
    color: 'var(--color-text)',
    margin: 0,
  };

  const addBtnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '12px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-caption)',
    fontWeight: 'bold',
    border: 'none',
    boxShadow: '0 4px 15px rgba(107, 78, 255, 0.3)',
    cursor: 'pointer',
    transition: 'transform 0.2s, background-color 0.2s',
  };

  const controlsPanelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '20px',
    backgroundColor: 'rgba(38, 40, 48, 0.4)',
    border: '1px solid rgba(231, 228, 223, 0.08)',
    borderRadius: '16px',
  };

  const searchWrapperStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px 14px 44px',
    backgroundColor: 'var(--color-background)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '10px',
    fontSize: 'var(--font-size-body)',
    color: 'var(--color-text)',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const searchIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '16px',
    opacity: 0.5,
  };

  const filterRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const tabGroupStyle: React.CSSProperties = {
    display: 'flex',
    backgroundColor: 'var(--color-background)',
    borderRadius: '8px',
    padding: '3px',
    border: '1px solid rgba(231, 228, 223, 0.08)',
  };

  const getTabStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: '6px',
    backgroundColor: active ? 'var(--color-surface)' : 'transparent',
    color: active ? 'var(--color-accent)' : 'var(--color-text)',
    fontWeight: active ? 'bold' : 'normal',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    opacity: active ? 1 : 0.7,
    transition: 'all 0.2s',
  });

  const filterDropdownsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  };

  const selectStyle: React.CSSProperties = {
    flex: '1 1 130px',
    padding: '10px 16px',
    backgroundColor: 'var(--color-background)',
    border: '1px solid rgba(231, 228, 223, 0.1)',
    borderRadius: '8px',
    color: 'var(--color-text)',
    fontSize: '13px',
    outline: 'none',
    cursor: 'pointer',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  };

  const emptyStateStyle: React.CSSProperties = {
    padding: '40px',
    textAlign: 'center',
    backgroundColor: 'rgba(38, 40, 48, 0.3)',
    borderRadius: '16px',
    border: '1px dashed rgba(231, 228, 223, 0.15)',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={pageHeaderStyle}>
        <h1 style={titleStyle}>フレーバーライブラリ</h1>
        <button
          type="button"
          style={addBtnStyle}
          onClick={() => setIsAddModalOpen(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.backgroundColor =
              'var(--color-primary-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.backgroundColor = 'var(--color-primary)';
          }}
        >
          <Plus size={16} />
          新規フレーバー登録
        </button>
      </div>

      {/* Search & Filters Dashboard */}
      <div style={controlsPanelStyle}>
        <div style={searchWrapperStyle}>
          <Search size={18} style={searchIconStyle} />
          <input
            type="text"
            placeholder="フレーバーやブランド等で検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={searchInputStyle}
            onFocus={(e) =>
              (e.currentTarget.style.borderColor = 'var(--color-primary)')
            }
            onBlur={(e) =>
              (e.currentTarget.style.borderColor = 'rgba(231, 228, 223, 0.1)')
            }
          />
        </div>

        <div style={filterRowStyle}>
          {/* Tabs for Ownership & Quick Edit Mode */}
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={tabGroupStyle}>
              <button
                type="button"
                style={getTabStyle(ownershipFilter === 'all')}
                onClick={() => setOwnershipFilter('all')}
              >
                すべて
              </button>
              <button
                type="button"
                style={getTabStyle(ownershipFilter === 'owned')}
                onClick={() => setOwnershipFilter('owned')}
              >
                所持のみ
              </button>
              <button
                type="button"
                style={getTabStyle(ownershipFilter === 'unowned')}
                onClick={() => setOwnershipFilter('unowned')}
              >
                未所持
              </button>
            </div>
            <button
              type="button"
              onClick={() => setIsQuickEditMode(!isQuickEditMode)}
              style={{
                ...getTabStyle(isQuickEditMode),
                backgroundColor: isQuickEditMode ? 'var(--color-accent)' : 'var(--color-surface)',
                color: isQuickEditMode ? 'var(--color-background)' : 'var(--color-text)',
                border: isQuickEditMode ? '1px solid var(--color-accent)' : '1px solid rgba(231, 228, 223, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              title="ONにすると、フレーバーカードのクリックで所持状態を即座に切り替えられます"
            >
              一括チェックモード {isQuickEditMode ? 'ON' : 'OFF'}
            </button>
            {ownershipFilter === 'owned' && processedFlavors.length > 0 && (
              <button
                type="button"
                onClick={handleBulkUnown}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255, 100, 100, 0.1)',
                  color: '#ff6b6b',
                  fontWeight: 'bold',
                  border: '1px solid rgba(255, 100, 100, 0.3)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 100, 100, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 100, 100, 0.1)';
                }}
                title="表示されているフレーバーを全て未所持にします"
              >
                表示中を全て未所持に
              </button>
            )}
          </div>

          {/* Selector filters & Sorts */}
          <div style={filterDropdownsStyle}>
            {/* Brand Dropdown */}
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              style={selectStyle}
            >
              <option value="all">ブランド： すべて</option>
              {[...brands]
                .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
                .map((b) => (
                  <option key={b.id} value={b.name}>
                    {b.name}
                  </option>
                ))}
            </select>

            {/* Series Dropdown */}
            <select
              value={selectedSeries}
              onChange={(e) => setSelectedSeries(e.target.value)}
              style={selectStyle}
            >
              <option value="all">系　　統： すべて</option>
              {series.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>

            {/* Nicotine Filter Dropdown */}
            <select
              value={nicotineFilter}
              onChange={(e) => setNicotineFilter(e.target.value as any)}
              style={selectStyle}
            >
              <option value="all">ニコチン： すべて</option>
              <option value="nicotine">ニコチン： あり</option>
              <option value="nonNicotine">ニコチン： なし</option>
            </select>

            {/* Sort Criteria */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              style={selectStyle}
            >
              <option value="name">並び替え： 名前順</option>
              <option value="brand">並び替え： ブランド順</option>
              <option value="rating">並び替え： 評価順</option>
            </select>
          </div>
        </div>
      </div>

      {/* Flavor Grid */}
      {paginatedFlavors.length > 0 ? (
        <div style={gridStyle}>
          {paginatedFlavors.map((flavor) => (
            <FlavorCard
              key={flavor.id}
              flavor={flavor}
              onClick={async () => {
                if (isQuickEditMode) {
                  try {
                    const newOwnedState = await FlavorRepository.toggleOwnership(flavor.id);
                    // 画面上のデータも更新する（再読み込みをせずに済ませる）
                    setFlavors((prev) =>
                      prev.map((f) =>
                        f.id === flavor.id ? { ...f, owned: newOwnedState } : f
                      )
                    );
                  } catch (err) {
                    console.error('Failed to toggle ownership:', err);
                  }
                } else {
                  setSelectedFlavor(flavor);
                }
              }}
            />
          ))}
        </div>
      ) : (
        <div style={emptyStateStyle}>
          <p
            style={{
              margin: 0,
              fontSize: '15px',
              color: 'rgba(231, 228, 223, 0.6)',
            }}
          >
            該当するフレーバーが見つかりませんでした。
          </p>
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        pageWindowStart={pageWindowStart}
        onPageWindowStartChange={setPageWindowStart}
      />

      {selectedFlavor && (
        <FlavorDetailModal
          flavor={selectedFlavor}
          onClose={() => setSelectedFlavor(null)}
          onSaveSuccess={loadData}
        />
      )}

      {/* Add Flavor Modal */}
      {isAddModalOpen && (
        <AddFlavorModal
          onClose={() => {
            setIsAddModalOpen(false);
            setInitialScannedText(undefined);
          }}
          onSaveSuccess={() => {
            loadData();
            setIsAddModalOpen(false);
            setInitialScannedText(undefined);
          }}
          initialScannedText={initialScannedText}
        />
      )}

      <ConfirmModal
        isOpen={confirmModalConfig.isOpen}
        title={confirmModalConfig.title}
        message={confirmModalConfig.message}
        onConfirm={confirmModalConfig.onConfirm}
        onCancel={() => setConfirmModalConfig((prev) => ({ ...prev, isOpen: false }))}
        isDestructive={true}
      />
    </div>
  );
}
